import moment from 'moment-hijri';
import {
  formatGregorianDateOnly,
  formatGregorianLabel,
  formatHijriLabel,
  isValidGregorianValue,
  parseStoredDateValue,
} from '../components/Forms/hijriDate';

export type AppDateFormat = 'dual' | 'gregorian' | 'hijri' | 'short';

export interface AppDateParts {
  gregorian: string;
  hijri: string;
}

/** تحويل أي قيمة تاريخ إلى YYYY-MM-DD ميلادي للتخزين والمقارنة */
export const toGregorianDateOnly = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    let s = value.trim();
    if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(s)) {
      const [y, mo, d] = s.split('/');
      s = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    const parsed = parseStoredDateValue(s);
    if (parsed?.isValid()) return formatGregorianDateOnly(parsed);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (s.includes('T')) return s.split('T')[0];
    const ddMmYyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
    if (ddMmYyyy) {
      const [, day, month, year] = ddMmYyyy;
      return `${year}-${month}-${day}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** مفتاح العرض: يحافظ على التاريخ الهجري المحفوظ كـ 1447-MM-DD */
const resolveDateLabelKey = (value: unknown): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    if (s.includes('T')) return s.split('T')[0];
  }
  return toGregorianDateOnly(value);
};

/** أجزاء العرض: ميلادي ثم هجري */
export const getAppDateParts = (value: unknown): AppDateParts | null => {
  const key = resolveDateLabelKey(value);
  if (!key) return null;

  const hijri = formatHijriLabel(key);
  const greg = formatGregorianLabel(key);
  if (!greg && !hijri) return null;
  return { gregorian: greg, hijri: hijri || '' };
};

/** عرض تاريخ واحد (ميلادي + هجري) — نصي للحالات البسيطة */
export const formatAppDate = (
  value: unknown,
  mode: AppDateFormat = 'dual'
): string => {
  if (value == null || value === '') return '';

  const parts = getAppDateParts(value);
  if (!parts) {
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('ar-SA-u-nu-latn', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  switch (mode) {
    case 'hijri':
      return parts.hijri || parts.gregorian;
    case 'gregorian':
      return parts.gregorian;
    case 'short': {
      const g = toGregorianDateOnly(value);
      return g && isValidGregorianValue(g)
        ? moment(g, 'YYYY-MM-DD').format('DD/MM/YYYY') + ' م'
        : parts.gregorian;
    }
    case 'dual':
    default:
      if (parts.hijri) return `${parts.gregorian}\n${parts.hijri}`;
      return parts.gregorian;
  }
};

/** تاريخ مع وقت */
export const formatAppDateTime = (value: unknown): string => {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('ar-SA-u-nu-latn', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const datePart = formatAppDate(d, 'dual');
  return datePart ? `${datePart}\n${time}` : time;
};

export const formatTodayDual = (): string => formatAppDate(new Date(), 'dual');
